import {useState, useCallback, type ReactNode} from 'react'
import {ChatInputDisplay} from './ChatInputDisplay'
import {CLIChatInputHeader} from './CLIChatInputHeader'
import {CLIStopButton} from './CLIStopButton'
import {PromptInput} from './PromptInput'
import {useCreateCommand} from '../../hooks/useCreateCommand'
import {usePromptResponseSubmission} from '../../hooks/use-prompt-response-submission'
import {computeConnectionStatus} from '../../utils/connection-status'
import {isActiveSession} from '../../types/session'
import type {Session} from '../../types/session'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {BlockedTaskInput} from './BlockedTaskInput'
import styles from './ChatInputDisplay.module.css'

interface CLIChatInputProps {
  /** The CLI session */
  session: Session
  /** Whether the CLI session supports remote steering. When false, shows a blocked message. */
  remoteSteerable?: boolean
  /** Whether the remote control (steering) policy is enabled by the org. When false, shows admin-contact message. */
  remoteControlPolicyEnabled?: boolean
  /** The agent task ID used in the CLI resume command */
  agentTaskId?: string
  /** The task-level stale_at deadline for computing connection status */
  staleAt?: string
  /** Timestamp of the last successful task query fetch (React Query dataUpdatedAt) */
  dataUpdatedAt?: number
  /** Callback when a command is successfully sent */
  onCommandSent?: () => void
  /** Whether the CLI remote session notice has been dismissed (from route query) */
  cliRemoteNoticeDismissed?: boolean
  /** Whether the task is shared — suppresses privacy banners for viewers */
  isShared?: boolean
  /** Optional profile-specific controls rendered in the chat input toolbar. */
  toolbarSlot?: ReactNode
}

/**
 * Input component for CLI sessions.
 * Shows PromptInput when there's a pending prompt, otherwise shows ChatInputDisplay.
 * Handles both regular message submission and prompt response submission.
 */
export function CLIChatInput({
  session,
  remoteSteerable,
  remoteControlPolicyEnabled,
  agentTaskId,
  staleAt,
  dataUpdatedAt,
  onCommandSent,
  cliRemoteNoticeDismissed,
  isShared,
  toolbarSlot,
}: CLIChatInputProps) {
  const connectionStatus = computeConnectionStatus(staleAt, true, session.state, dataUpdatedAt)
  const sessionId = session.id
  const [value, setValue] = useState('')

  const {pendingPrompt, isLoading, dismissPrompt, handlePromptRespond} = usePromptResponseSubmission({
    sessionId,
    enabled: true,
  })

  const {createCommand, isPending} = useCreateCommand()

  const handleSubmit = useCallback(() => {
    if (!value.trim() || isPending) return

    createCommand({
      sessionId,
      content: value.trim(),
    })

    setValue('')
    onCommandSent?.()
  }, [value, isPending, sessionId, createCommand, onCommandSent])

  if (!isActiveSession(session)) {
    const resumeCommand = agentTaskId ? `copilot --remote --resume ${agentTaskId}` : 'copilot --remote --resume'
    return (
      <div className={styles.inputWrapper}>
        <BlockedTaskInput>
          Session has ended. Reconnect with <strong>{resumeCommand}</strong>
        </BlockedTaskInput>
      </div>
    )
  }

  if (remoteControlPolicyEnabled === false) {
    return (
      <>
        <CLIChatInputHeader agentTaskId={agentTaskId} connectionStatus={connectionStatus} isShared={isShared} />
        <div className={styles.inputWrapper}>
          <BlockedTaskInput>
            Steering disabled. Contact your administrator to enable steering on local sessions.
          </BlockedTaskInput>
        </div>
      </>
    )
  }

  if (remoteSteerable === false) {
    return (
      <>
        <CLIChatInputHeader agentTaskId={agentTaskId} connectionStatus={connectionStatus} isShared={isShared} />
        <div className={styles.inputWrapper}>
          <BlockedTaskInput>
            Steering disabled. Type <strong>/remote on</strong> to enable steering. To enable steering for a new session
            start it with <strong>copilot --remote</strong>
          </BlockedTaskInput>
        </div>
      </>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.skeletonWrapper}>
          <LoadingSkeleton variant="rounded" height="100%" width="100%" />
        </div>
      </div>
    )
  }

  if (pendingPrompt) {
    return (
      <>
        <CLIChatInputHeader
          agentTaskId={agentTaskId}
          connectionStatus={connectionStatus}
          cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
          isShared={isShared}
        />
        <PromptInput
          key={pendingPrompt.toolCallId}
          prompt={pendingPrompt}
          onRespond={handlePromptRespond}
          onDismiss={dismissPrompt}
        />
      </>
    )
  }

  return (
    <>
      <CLIChatInputHeader
        agentTaskId={agentTaskId}
        connectionStatus={connectionStatus}
        cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
        isShared={isShared}
      />
      <ChatInputDisplay
        value={value}
        onChange={setValue}
        onSubmit={handleSubmit}
        placeholder="Send a message to the CLI session..."
        isSubmitting={isPending}
        toolbarSlot={toolbarSlot}
        stopSlot={<CLIStopButton session={session} />}
      />
    </>
  )
}
